/**
 * JalaliDatePicker.js
 * - کلیک روی عنوان → انتخاب ماه → انتخاب سال
 * - ورود دستی تاریخ: با خروج از فیلد یا Enter ورودی کوتاه تکمیل می‌شود:
 *     «5» ⇒ روز ۵ ماه جاری ، «0105» ⇒ ۰۱/۰۵ سال جاری ، «14020105» ⇒ ۱۴۰۲/۰۱/۰۵ ، ارقام فارسی
 * - تقویم با portal و position:fixed روی body رندر می‌شود تا داخل مودال‌های اسکرول‌دار
 *   بریده نشود؛ اگر پایین جا نباشد بالای فیلد باز می‌شود.
 * - دکمهٔ ✕ برای خالی کردن تاریخ (تاریخ خالی مجاز است).
 * مسیر: frontend/src/components/JalaliDatePicker.js
 */
 import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
 import { createPortal } from 'react-dom';

 /* ── تبدیل‌ها ── */
 function toJalali(gy, gm, gd) {
   let jy = gy <= 1600 ? 0 : 979;
   gy -= gy <= 1600 ? 621 : 1600;
   let gYm = gm - 2;
   if (gYm <= 0) { gYm += 12; gy--; }
   let g_day_no = 365*gy + Math.floor((gy+3)/4) - Math.floor((gy+99)/100) + Math.floor((gy+399)/400);
   for (let i = 0; i < gYm-1; i++) g_day_no += [31,28,31,30,31,30,31,31,30,31,30,31][i];
   if (gYm > 1 && ((gy%4===0 && gy%100!==0) || gy%400===0)) g_day_no++;
   g_day_no += gd - 1;
   let j_day_no = g_day_no - 79;
   let j_np = Math.floor(j_day_no/12053); j_day_no %= 12053;
   jy += 33*j_np + 4*Math.floor(j_day_no/1461);
   j_day_no %= 1461;
   if (j_day_no >= 366) { jy += Math.floor((j_day_no-1)/365); j_day_no = (j_day_no-1)%365; }
   let jm, jd;
   const months = [31,31,31,31,31,31,30,30,30,30,30,29];
   for (jm = 0; jm < 11 && j_day_no >= months[jm]; jm++) j_day_no -= months[jm];
   jd = j_day_no + 1;
   return [jy, jm+1, jd];
 }
 
 function toGregorian(jy, jm, jd) {
   jy += 1595;
   let days = -355779 + 365*jy + Math.floor(jy/33)*8 + Math.floor(((jy%33)+3)/4) + jd;
   for (let i = 1; i < jm; i++) days += i <= 6 ? 31 : 30;
   let gy = 400*Math.floor(days/146097); days %= 146097;
   if (days > 36524) { gy += 100*Math.floor(--days/36524); days %= 36524; if (days >= 365) days++; }
   gy += 4*Math.floor(days/1461); days %= 1461;
   if (days > 365) { gy += Math.floor((days-1)/365); days = (days-1)%365; }
   let gd = days + 1;
   const months = [0,31,29,31,30,31,30,31,31,30,31,30,31];
   if ((gy%4!==0)||(gy%100===0 && gy%400!==0)) months[2]=28;
   let gm;
   for (gm = 1; gm <= 12 && gd > months[gm]; gm++) gd -= months[gm];
   return [gy, gm, gd];
 }
 
 function daysInJalaliMonth(jy, jm) {
   if (jm <= 6) return 31;
   if (jm <= 11) return 30;
   return jy % 4 === 3 ? 30 : 29;
 }
 
 function firstDayOfMonth(jy, jm) {
   const [gy, gm, gd] = toGregorian(jy, jm, 1);
   return (new Date(gy, gm-1, gd).getDay() + 1) % 7;
 }
 
 const fa = n => String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
 const MONTHS = ['فروردین','اردیبهشت','خرداد','تیر','مرداد','شهریور','مهر','آبان','آذر','دی','بهمن','اسفند'];
 const DAYS   = ['ش','ی','د','س','چ','پ','ج'];
 
 function parseJalali(str) {
   if (!str || typeof str !== 'string') return null;
   const parts = str.split('/').map(Number);
   if (parts.length !== 3 || parts.some(isNaN)) return null;
   return { y: parts[0], m: parts[1], d: parts[2] };
 }
 function formatJalali(y, m, d) {
   return `${y}/${String(m).padStart(2,'0')}/${String(d).padStart(2,'0')}`;
 }
 
 /* ── SetDate — تکمیل ورودی کوتاه ── */
 const toLatin = t => String(t ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));
 export function setDate(dt, today) {
   const raw = String(dt ?? '').trim();
   if (raw === '' || raw === '0') return raw;
   let d = toLatin(raw).replace(/\//g, '').replace(/[^0-9]/g, '');
   d = d ? String(parseInt(d, 10)) : '';
   const L = d.length;
   if (L === 1) d = d.padStart(2, '0');
   else if (L === 2) { if (d >= '32' || (d >= '31' && +today.slice(5, 7) >= 8)) d = '0' + d[0] + '0' + d.slice(-1); }
   else if (L === 3) {
     if (d.slice(-2) < '31' || (d.slice(-2) === '31' && d[0] <= '6')) d = d.padStart(4, '0');
     else if (d.slice(-2) > '31' || (d.slice(-2) === '31' && d[0] > '6')) d = d.slice(0, 2) + '0' + d.slice(-1);
     if (d.slice(0, 2) > '12') d = d[0] + '0' + d.slice(-3);
   } else if (L === 4) {
     if (d.slice(-2) > '31' || (d.slice(-2) === '31' && d[0] > '6')) d = d.slice(0, 3) + '0' + d.slice(-1);
     else if (d.slice(0, 2) > '12') d = d[0] + '0' + d.slice(-3);
   }
   if (!d || +d === 0) return '';
   const tdd = +today.slice(8, 10), tmm = +today.slice(5, 7), tyy = +today.slice(0, 4);
   let sdt;
   if (d.length < 3 && +d <= tdd) sdt = today.slice(0, 8) + d.padStart(2, '0');
   else if (d.length < 3 && +d > tdd && +d <= 31) {
     d = d.padStart(2, '0'); const pm = tmm - 1;
     sdt = pm === 0 ? `${tyy - 1}/12/${d}` : today.slice(0, 5) + String(pm).padStart(2, '0') + '/' + d;
   } else if (d.length < 3) return '';
   else if (d.length < 5) {
     if (+d.slice(-2) >= 1 && +d.slice(-2) <= 31 && +d.slice(0, 2) <= 12) { d = d.padStart(4, '0'); sdt = today.slice(0, 5) + d.slice(0, 2) + '/' + d.slice(2, 4); }
     else return d;
   } else if (d.length < 7) {
     if (d.length === 5) sdt = today.slice(0, 3) + d[0] + '/' + d.slice(1, 3) + '/' + d.slice(3, 5);
     else sdt = (+d.slice(0, 2) < 10 ? today.slice(0, 2) : String(+today.slice(0, 2) - 1)) + d.slice(0, 2) + '/' + d.slice(2, 4) + '/' + d.slice(4, 6);
   } else if (d.length === 7) sdt = '1' + d.slice(0, 3) + '/' + d.slice(3, 5) + '/' + d.slice(5, 7);
   else if (d.length === 8) sdt = d.slice(0, 4) + '/' + d.slice(4, 6) + '/' + d.slice(6, 8);
   else sdt = d;
   if (sdt.length === 10) {
     const yy = +sdt.slice(0, 4), mm = +sdt.slice(5, 7); let dd = +sdt.slice(8, 10);
     if (mm === 12) {
       if (dd > 30) { dd = 30; sdt = sdt.slice(0, 8) + '30'; }
       if (dd === 30 && Math.abs(yy - 1399) % 4 !== 0) {
         window.alert('اخطار\nخطا در تاریخ\n فقط سال کبیسه 30 روز دارد');
         sdt = sdt.slice(0, 8) + '29';
       }
     }
   }
   return sdt;
 }

 /* ════════════════════════════════════════════════════ */
 export default function JalaliDatePicker({ value, onChange, placeholder='انتخاب تاریخ', disabled=false }) {
   const today  = (() => { const n = new Date(); return toJalali(n.getFullYear(), n.getMonth()+1, n.getDate()); })();
   const parsed = parseJalali(value);
 
   const [open,  setOpen]  = useState(false);
   const [viewY, setViewY] = useState(parsed ? parsed.y : today[0]);
   const [viewM, setViewM] = useState(parsed ? parsed.m : today[1]);
   // mode: 'day' | 'month' | 'year'
   const [mode,  setMode]  = useState('day');
   // برای انتخاب سال: شروع بازه نمایش (12 سال در یک صفحه)
   const [yearPage, setYearPage] = useState(Math.floor((parsed ? parsed.y : today[0]) / 12) * 12);
 
   const ref = useRef(null);
   const popRef = useRef(null);
   const [text, setText] = useState('');
   const [pos, setPos] = useState({ top: 0, left: 0, up: false });
   const todayStr = formatJalali(today[0], today[1], today[2]);

   useEffect(() => {
     const handler = e => {
       const inField = ref.current && ref.current.contains(e.target);
       const inPop = popRef.current && popRef.current.contains(e.target);
       if (!inField && !inPop) { setOpen(false); setMode('day'); }
     };
     document.addEventListener('mousedown', handler);
     return () => document.removeEventListener('mousedown', handler);
   }, []);

   // متن فیلد با value هماهنگ می‌ماند (تا کاربر در حال تایپ نیست)
   useEffect(() => { setText(value || ''); }, [value]);

   // جای تقویم: position:fixed نسبت به فیلد؛ اگر پایین جا نیست، بالا باز می‌شود
   const place = () => {
     if (!ref.current) return;
     const r = ref.current.getBoundingClientRect();
     const W = 288, H = 330;
     const up = window.innerHeight - r.bottom < H + 8 && r.top > H + 8;
     let left = r.right - W;                        // راست‌چین با فیلد
     left = Math.max(8, Math.min(left, window.innerWidth - W - 8));
     setPos({ top: up ? r.top - H - 4 : r.bottom + 4, left, up });
   };
   useLayoutEffect(() => {
     if (!open) return undefined;
     place();
     const h = () => place();
     window.addEventListener('resize', h);
     window.addEventListener('scroll', h, true);
     return () => { window.removeEventListener('resize', h); window.removeEventListener('scroll', h, true); };
   }, [open]); // eslint-disable-line

   const commitText = () => {
     const v = setDate(text, todayStr);
     setText(v);
     if (v !== (value || '')) onChange(v);
   };
 
   // وقتی value از بیرون عوض شد view رو sync کن
   useEffect(() => {
     if (parsed) { setViewY(parsed.y); setViewM(parsed.m); setYearPage(Math.floor(parsed.y/12)*12); }
   }, [value]); // eslint-disable-line
 
   const selectDay = d => { onChange(formatJalali(viewY, viewM, d)); setOpen(false); setMode('day'); };
   const selectMonth = m => { setViewM(m); setMode('day'); };
   const selectYear  = y => { setViewY(y); setYearPage(Math.floor(y/12)*12); setMode('month'); };
 
   const prevMonth = () => { if (viewM===1){setViewY(viewY-1);setViewM(12);}else setViewM(viewM-1); };
   const nextMonth = () => { if (viewM===12){setViewY(viewY+1);setViewM(1);}else setViewM(viewM+1); };
 
   // ── محاسبه سلول‌های روز ──
   const days     = daysInJalaliMonth(viewY, viewM);
   const firstDay = firstDayOfMonth(viewY, viewM);
   const cells    = [];
   for (let i = 0; i < firstDay; i++) cells.push(null);
   for (let d = 1; d <= days; d++) cells.push(d);
 
   const isSelected = parsed && parsed.y===viewY && parsed.m===viewM;
   const isToday    = d => viewY===today[0] && viewM===today[1] && d===today[2];
 
 
   // 12 سال برای نمایش در صفحه سال
   const yearRange = Array.from({length:12}, (_,i) => yearPage + i);
 
   return (
     <div ref={ref} style={{position:'relative', width:'100%'}}>
       {/* ورودی — قابل تایپ دستی؛ SetDate هنگام خروج یا Enter */}
       <div style={{
           display:'flex', alignItems:'center', border:'1.5px solid #e0e0e0', borderRadius:'8px',
           background: disabled?'#f5f5f5':'#fff', minHeight:'38px', overflow:'hidden',
         }}>
         <input
           value={text}
           disabled={disabled}
           placeholder={placeholder}
           dir="ltr"
           onChange={e => setText(e.target.value)}
           onBlur={commitText}
           onKeyDown={e => {
             if (e.key === 'Enter') { e.preventDefault(); commitText(); setOpen(false); }
             if (e.key === 'Escape') { setOpen(false); setText(value || ''); }
             if (e.key === 'F4' || (e.altKey && e.key === 'ArrowDown')) { e.preventDefault(); setOpen(true); }
           }}
           style={{
             flex:1, minWidth:0, border:'none', outline:'none', background:'transparent',
             padding:'8px 10px', fontSize:'13px', fontFamily:"'Vazirmatn',Tahoma,sans-serif",
             color:'#1a2f50', textAlign:'right',
           }}/>
         {!disabled && text && (
           <button type="button" title="پاک کردن تاریخ" tabIndex={-1}
             onMouseDown={e => e.preventDefault()}
             onClick={() => { setText(''); onChange(''); }}
             style={{border:'none', background:'transparent', color:'#c62828', cursor:'pointer',
               fontSize:'13px', padding:'0 4px'}}>✕</button>
         )}
         <button type="button" tabIndex={-1} disabled={disabled}
           onMouseDown={e => e.preventDefault()}
           onClick={() => !disabled && setOpen(o => { if (!o) setMode('day'); return !o; })}
           style={{border:'none', borderRight:'1px solid #eee', background:'transparent',
             cursor: disabled?'default':'pointer', fontSize:'16px', color:'#C8A84B', padding:'0 10px',
             alignSelf:'stretch'}}>📅</button>
       </div>

       {open && createPortal(
         <div ref={popRef} style={{
           position:'fixed', top: pos.top, left: pos.left, zIndex:20000,
           background:'#fff', borderRadius:'12px', boxShadow:'0 8px 32px rgba(0,0,0,0.22)',
           width:'288px', direction:'rtl', overflow:'hidden', border:'1px solid #e0e0e0',
         }}>
 
           {/* ── هدر — کلیک‌پذیر برای تغییر mode ── */}
           <div style={{background:'linear-gradient(135deg,#0a1628,#1a2f50)', padding:'12px 14px',
             display:'flex', alignItems:'center', justifyContent:'space-between'}}>
 
             {mode === 'day' && (
               <>
                 <button onClick={nextMonth} style={navBtn}>›</button>
                 <div
                   onClick={() => setMode('month')}
                   style={{color:'#fff', fontWeight:'700', fontSize:'14px', textAlign:'center',
                     cursor:'pointer', padding:'2px 10px', borderRadius:'6px',
                     background:'rgba(255,255,255,0.1)',
                     display:'flex', alignItems:'center', gap:'6px'}}
                   title="کلیک برای انتخاب ماه">
                   <span>{MONTHS[viewM-1]}</span>
                   <span style={{color:'#C8A84B'}}>{fa(viewY)}</span>
                   <span style={{fontSize:'10px', opacity:0.7}}>▼</span>
                 </div>
                 <button onClick={prevMonth} style={navBtn}>‹</button>
               </>
             )}
 
             {mode === 'month' && (
               <>
                 <button onClick={() => setMode('year')} style={{...navBtn, width:'auto', padding:'0 8px', fontSize:'12px'}}>
                   {fa(viewY)} ▼
                 </button>
                 <div style={{color:'#fff', fontWeight:'700', fontSize:'13px'}}>انتخاب ماه</div>
                 <button onClick={() => setMode('day')} style={{...navBtn, fontSize:'13px'}}>✕</button>
               </>
             )}
 
             {mode === 'year' && (
               <>
                 <button onClick={() => setYearPage(yearPage+12)} style={navBtn}>›</button>
                 <div style={{color:'#fff', fontWeight:'700', fontSize:'13px'}}>
                   {fa(yearPage)} — {fa(yearPage+11)}
                 </div>
                 <button onClick={() => setYearPage(yearPage-12)} style={navBtn}>‹</button>
               </>
             )}
           </div>
 
           {/* ── حالت انتخاب ماه ── */}
           {mode === 'month' && (
             <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'6px', padding:'12px'}}>
               {MONTHS.map((m, i) => (
                 <div key={i} onClick={() => selectMonth(i+1)}
                   style={{
                     textAlign:'center', padding:'8px 4px', borderRadius:'8px',
                     cursor:'pointer', fontSize:'12px', fontWeight:'600',
                     background: viewM===i+1 ? '#1a2f50' : '#f5f5f5',
                     color:      viewM===i+1 ? '#C8A84B'  : '#333',
                     border: viewM===i+1 ? '2px solid #C8A84B' : '2px solid transparent',
                     transition:'all 0.15s',
                   }}
                   onMouseEnter={e => { if(viewM!==i+1) e.currentTarget.style.background='#e8f0fe'; }}
                   onMouseLeave={e => { if(viewM!==i+1) e.currentTarget.style.background='#f5f5f5'; }}>
                   {m}
                 </div>
               ))}
             </div>
           )}
 
           {/* ── حالت انتخاب سال ── */}
           {mode === 'year' && (
             <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'6px', padding:'12px'}}>
               {yearRange.map(y => (
                 <div key={y} onClick={() => selectYear(y)}
                   style={{
                     textAlign:'center', padding:'8px 2px', borderRadius:'8px',
                     cursor:'pointer', fontSize:'12px', fontWeight:'600',
                     background: viewY===y ? '#1a2f50' : y===today[0] ? '#e8f0fe' : '#f5f5f5',
                     color:      viewY===y ? '#C8A84B'  : y===today[0] ? '#1a2f50' : '#333',
                     border: viewY===y ? '2px solid #C8A84B' : '2px solid transparent',
                     transition:'all 0.15s',
                   }}
                   onMouseEnter={e => { if(viewY!==y) e.currentTarget.style.background='#e8f0fe'; }}
                   onMouseLeave={e => { if(viewY!==y) e.currentTarget.style.background=viewY===y?'#1a2f50':y===today[0]?'#e8f0fe':'#f5f5f5'; }}>
                   {fa(y)}
                 </div>
               ))}
             </div>
           )}
 
           {/* ── حالت انتخاب روز ── */}
           {mode === 'day' && (
             <>
               {/* روزهای هفته */}
               <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)',
                 background:'#f0f2f7', padding:'6px 8px 4px'}}>
                 {DAYS.map(d => (
                   <div key={d} style={{textAlign:'center', fontSize:'11px',
                     fontWeight:'700', color:'#888', padding:'3px 0'}}>{d}</div>
                 ))}
               </div>
 
               {/* روزها */}
               <div style={{display:'grid', gridTemplateColumns:'repeat(7,1fr)',
                 padding:'4px 8px 10px', gap:'2px'}}>
                 {cells.map((d, i) => (
                   <div key={i} onClick={() => d && selectDay(d)}
                     style={{
                       textAlign:'center', padding:'5px 2px', borderRadius:'6px',
                       fontSize:'12px', cursor: d?'pointer':'default',
                       fontWeight: isToday(d)||(isSelected&&parsed.d===d) ? '700' : '400',
                       background: isSelected&&parsed.d===d ? '#1a2f50'
                         : isToday(d) ? '#e8f0fe' : 'transparent',
                       color: isSelected&&parsed.d===d ? '#C8A84B'
                         : isToday(d) ? '#1a2f50' : d?'#333':'transparent',
                       transition:'background 0.1s',
                     }}
                     onMouseEnter={e => { if(d&&!(isSelected&&parsed.d===d)) e.currentTarget.style.background='#f5f5f5'; }}
                     onMouseLeave={e => { if(d&&!(isSelected&&parsed.d===d)) e.currentTarget.style.background='transparent'; }}>
                     {d ? fa(d) : ''}
                   </div>
                 ))}
               </div>
 
               {/* دکمه امروز */}
               <div style={{padding:'8px', borderTop:'1px solid #f0f2f7', textAlign:'center'}}>
                 <button
                   onClick={() => { setViewY(today[0]); setViewM(today[1]); selectDay(today[2]); }}
                   style={{padding:'5px 16px', background:'#e8f0fe', color:'#1a2f50',
                     border:'none', borderRadius:'6px', cursor:'pointer',
                     fontFamily:'inherit', fontSize:'12px', fontWeight:'700'}}>
                   امروز — {fa(today[0])}/{fa(String(today[1]).padStart(2,'0'))}/{fa(String(today[2]).padStart(2,'0'))}
                 </button>
               </div>
             </>
           )}
         </div>,
         document.body
       )}
     </div>
   );
 }
 
 const navBtn = {
   background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)',
   color:'#fff', width:'28px', height:'28px', borderRadius:'50%',
   cursor:'pointer', fontSize:'16px', display:'flex', alignItems:'center',
   justifyContent:'center', lineHeight:1,
 };